import {DependencyList, Dispatch, SetStateAction, useCallback, useEffect, useState} from "react";

interface AsyncState<T> {
    data: T | undefined;
    error: unknown;
    loading: boolean;
    reload(): void;
    setData: Dispatch<SetStateAction<T | undefined>>;
}

// Loads data for a page; results of superseded loads are dropped.
export function useAsync<T>(load: () => Promise<T>, deps: DependencyList): AsyncState<T> {
    const [data, setData] = useState<T>();
    const [error, setError] = useState<unknown>();
    const [loading, setLoading] = useState(true);
    const [generation, setGeneration] = useState(0);

    useEffect(() => {
        let current = true;
        setLoading(true);
        setError(undefined);
        load().then(
            result => { if (current) setData(result); },
            err => { if (current) setError(err); }
        ).finally(() => { if (current) setLoading(false); });
        return () => { current = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, generation]);

    const reload = useCallback(() => setGeneration(x => x + 1), []);
    return { data, error, loading, reload, setData };
}
